import { useMemo, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";

import { ListLoader, Icon } from "components/atoms";
import { ExpenseTableItem } from "components/molecules";
import { ColumnSettingsModal } from "components/organisms";
import { useColumnPreferences } from "hooks/useColumnPreferences";

import {
	getPropertyExpenses,
	getPropertyExpensesCount,
	getPropertyExpensesList,
	getPropertyExpensesLoading,
	getPropertyExpensesTotal,
} from "state/modules/properties";

import { Expense } from "interfaces/expenses";
import { IconName } from "interfaces/icons";

import { MAIN_THEME } from "styles/colors";
import { FONT_FAMILY_MEDIUM } from "styles/typography";

const ExpensesList = styled(InfiniteScroll as any)`
	display: grid;
	grid-template-columns: 1fr;
	width: 100%;
	gap: 0;
	overflow: hidden !important;
	background: transparent;
`;

const Container = styled.div`
	padding: 15px 30px 40px;
	overflow-y: auto;
	
	@media (max-width: 768px) {
		padding: 15px 10px 70px;
	}

	@media (max-width: 480px) {
		padding: 15px 5px 70px;
	}
`;

const ScrollWrapper = styled.div`
	width: 100%;
	overflow-x: auto;
	overflow-y: visible;
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
	border-radius: 12px;
	background: transparent;

	/* Custom scrollbar styling */
	&::-webkit-scrollbar {
		height: 8px;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		background: ${MAIN_THEME.DARK_BG_TERTIARY};
		border-radius: 4px;

		&:hover {
			background: ${MAIN_THEME.GREY};
		}
	}
`;

const TableWrapper = styled.div<{ mobileMinWidth: string }>`
	min-width: 900px;

	@media (max-width: 768px) {
		min-width: ${(props) => props.mobileMinWidth};
	}
`;


const TableHeaderItem = styled.div`
	font-size: 14px;
	text-align: left;
	display: flex;
	justify-content: flex-start;
	align-items: center;
		height: 52px;
		padding: 10px 8px;
		color: ${MAIN_THEME.TEXT_SECONDARY};

			background: transparent;


	font-family: ${FONT_FAMILY_MEDIUM};
	font-weight: 500;
	text-transform: none;
	letter-spacing: 0.3px;
	border-bottom: 1px solid ${MAIN_THEME.BORDER_COLOR};



		@media (max-width: 768px) {
			font-size: 12px;
			height: 36px;
			padding: 8px 6px;
		}

`;

const TableHeader = styled.div<{ columns?: string }>`
	display: grid;
	grid-template-columns: ${(props) => props.columns || "1fr 1fr 1fr 1fr 1fr 0.5fr"};
	gap: 0;
	margin-bottom: 0;
	overflow: hidden;
	background: transparent;


`;

const SettingsButton = styled.button`
	border: none;
	background: transparent;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 10px;
	margin-left: auto;

	&:hover {
		opacity: 0.8;
	}
`;

const ContentHeader = styled.div`
	display: flex;
	justify-content: flex-end;
`;

const DEFAULT_COLUMNS = [
	{ id: 'name', label: 'Name', isVisible: true, width: '1fr' },
	{ id: 'description', label: 'Description', isVisible: true, width: '1fr' },
	{ id: 'amount', label: 'Amount', isVisible: true, width: '1fr' },
	{ id: 'currency', label: 'Currency', isVisible: true, width: '1fr' },
	{ id: 'date', label: 'Date', isVisible: true, width: '1fr' },
	{ id: 'receipt', label: 'Receipt', isVisible: true, width: '0.5fr' },
];

const EmptyListMessage = styled.p`
	font-family: ${FONT_FAMILY_MEDIUM};
	color: ${MAIN_THEME.TEXT_SECONDARY};
	font-size: 16px;
	background: ${MAIN_THEME.DARK_BG_TERTIARY};
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
	border-radius: 8px;
	padding: 15px;
	text-align: center;
`;

interface Props {
	propertyId: string;
}

const PropertyExpensesList = (props: Props): JSX.Element => {
	const { propertyId } = props;
	const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

	const { columns, toggleColumn } = useColumnPreferences(
		"propertyExpensesList",
		DEFAULT_COLUMNS
	);

	const visibleColumns = useMemo(
		() => columns.filter((col) => col.isVisible),
		[columns]
	);
	const visibleColumnIds = useMemo(
		() => visibleColumns.map((c) => c.id),
		[visibleColumns]
	);
	const gridTemplateColumns = useMemo(() => {
		return visibleColumns.map((c) => c.width).join(" ");
	}, [visibleColumns]);

	const mobileMinWidth = useMemo(() => {
		return `${Math.max(visibleColumns.length * 120, 100)}px`;
	}, [visibleColumns]);

	const isColumnVisible = (id: string) => visibleColumnIds.includes(id);

	const expensesList = useSelector(getPropertyExpensesList);
	const expensesTotal = useSelector(getPropertyExpensesTotal);
	const expensesCount = useSelector(getPropertyExpensesCount);
	const isPropertyExpensesLoading = useSelector(getPropertyExpensesLoading);

	const dispatch = useDispatch();

	const hasMore = useMemo(
		() => expensesCount < expensesTotal,
		[expensesCount, expensesTotal]
	);

	const loadMore = () => {
		if (!isPropertyExpensesLoading) {
			dispatch(getPropertyExpenses(propertyId, true));
		}
	};

	const renderExpensesList = () => (
		<ExpensesList
			scrollableTarget="propertyDetailsListContainer"
			dataLength={expensesCount}
			next={loadMore}
			hasMore={hasMore}
			loader={<ListLoader />}
		>
			{expensesList.map((expense: Expense) => (
				<ExpenseTableItem
					data={expense}
					key={expense.id}
					visibleColumnIds={visibleColumnIds}
					gridTemplateColumns={gridTemplateColumns}
				/>
			))}
		</ExpensesList>
	);

	const renderTableHeader = () =>
		expensesList.length ? (
			<TableHeader columns={gridTemplateColumns}>
				{isColumnVisible('name') && <TableHeaderItem>Name</TableHeaderItem>}
				{isColumnVisible('description') && <TableHeaderItem>Description</TableHeaderItem>}
				{isColumnVisible('amount') && <TableHeaderItem>Amount</TableHeaderItem>}
				{isColumnVisible('currency') && <TableHeaderItem>Currency</TableHeaderItem>}
				{isColumnVisible('date') && <TableHeaderItem>Date</TableHeaderItem>}
				{isColumnVisible('receipt') && <TableHeaderItem>Receipt</TableHeaderItem>}
			</TableHeader>
		) : (
			<EmptyListMessage>Empty list</EmptyListMessage>
		);

	return (
		<Container>
			<ContentHeader>
				<SettingsButton onClick={() => setIsSettingsModalOpen(true)}>
					<Icon name={IconName.SETTINGS} color={MAIN_THEME.TEXT_PRIMARY} size={20} />
				</SettingsButton>
			</ContentHeader>
			<ScrollWrapper>
				<TableWrapper mobileMinWidth={mobileMinWidth}>
					{renderTableHeader()}
					{renderExpensesList()}
				</TableWrapper>
			</ScrollWrapper>
			<ColumnSettingsModal
				isVisible={isSettingsModalOpen}
				close={() => setIsSettingsModalOpen(false)}
				columns={columns}
				onToggle={toggleColumn}
			/>
		</Container>
	);
};

export default PropertyExpensesList;
